import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Central keyboard shortcut registry (PRD §12).
 *
 * Screens register scoped shortcut sets on mount; the topmost (most recently
 * mounted — e.g. an open modal) scope wins on a conflicting binding, so a
 * modal's `Esc` shadows the app shell's `Esc` without either side needing to
 * know about the other. Later phases add bindings by calling `useShortcuts`
 * from wherever they live — no new listeners, no central switch statement.
 */

export interface KeyBinding {
  key: string; // KeyboardEvent.key, case-insensitive for letters, exact for 'F1'.. 'Escape' etc.
  alt?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  meta?: boolean;
}

export interface ShortcutDef {
  id: string;
  bindings: KeyBinding[];
  /** Shown in the cheat sheet (Alt+H) and command palette. */
  description: string;
  handler: (e: KeyboardEvent) => void;
  /** Fire even while focus is in a text input/textarea/select. Default false. */
  allowInInputs?: boolean;
  /** Extra guard; shortcut is skipped entirely if this returns false. */
  when?: () => boolean;
  /** Hide from the cheat sheet (used for things like row-navigation arrows). */
  hidden?: boolean;
}

interface Scope {
  instanceId: string;
  label: string;
  /**
   * Held in a ref, not as state. A component's `defs` array is a fresh literal on every
   * render; storing it in the scope stack and notifying subscribers would make every
   * render of every component that registers shortcuts wake every component that reads
   * them — which loops as soon as one component does both (the cheat sheet does).
   * Push and pop change the stack; editing the bindings inside a scope does not.
   */
  shortcuts: { current: ShortcutDef[] };
}

let scopeStack: Scope[] = [];
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function snapshot() {
  return scopeStack;
}

function pushScope(label: string): Scope {
  const scope: Scope = { instanceId: crypto.randomUUID(), label, shortcuts: { current: [] } };
  scopeStack = [...scopeStack, scope];
  notify();
  return scope;
}

function popScope(scope: Scope) {
  scopeStack = scopeStack.filter((s) => s.instanceId !== scope.instanceId);
  notify();
}

function setScopeShortcuts(scope: Scope, shortcuts: ShortcutDef[]) {
  scope.shortcuts.current = shortcuts;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

function bindingMatches(binding: KeyBinding, e: KeyboardEvent): boolean {
  const key = binding.key.length === 1 ? binding.key.toLowerCase() : binding.key;
  const eventKeyRaw = e.key;
  const eventKey = key.length === 1 ? eventKeyRaw.toLowerCase() : eventKeyRaw;
  if (eventKey !== key) return false;
  if (Boolean(binding.alt) !== e.altKey) return false;
  if (Boolean(binding.shift) !== e.shiftKey) return false;
  // Ctrl bindings also fire on Cmd (mac-friendliness) unless `meta` is used instead.
  const ctrlLike = e.ctrlKey || e.metaKey;
  if (Boolean(binding.ctrl) !== ctrlLike) return false;
  return true;
}

let installed = false;

export function installGlobalKeyListener() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('keydown', (e) => {
    const editable = isEditableTarget(e.target);
    for (let i = scopeStack.length - 1; i >= 0; i--) {
      const scope = scopeStack[i];
      if (!scope) continue;
      for (const def of scope.shortcuts.current) {
        if (editable && !def.allowInInputs) continue;
        if (def.when && !def.when()) continue;
        if (def.bindings.some((b) => bindingMatches(b, e))) {
          e.preventDefault();
          e.stopPropagation();
          def.handler(e);
          return;
        }
      }
    }
  });
}

/**
 * Register a scoped set of shortcuts for the lifetime of the calling
 * component. `defs` may change across renders; the latest value is always
 * what fires (kept in a ref, not re-pushed on every render).
 */
export function useShortcuts(label: string, defs: ShortcutDef[]) {
  const scopeRef = useRef<Scope | null>(null);
  const defsRef = useRef(defs);
  defsRef.current = defs;

  useEffect(() => {
    // Register in the effect, rather than during render. React 18 StrictMode deliberately
    // runs effect setup -> cleanup -> setup once in development; keeping the scope reference
    // nullable lets the second setup create a fresh live scope after the first cleanup.
    const scope = pushScope(label);
    scopeRef.current = scope;
    setScopeShortcuts(scope, defsRef.current);
    return () => {
      popScope(scope);
      if (scopeRef.current === scope) scopeRef.current = null;
    };
  }, [label]);

  // Deliberately on every render and outside an effect: `defs` closes over the latest
  // props and state, and a keypress must never fire a stale handler. Mutating a ref is
  // not a subscription-visible change, so this cannot trigger a render.
  if (scopeRef.current) setScopeShortcuts(scopeRef.current, defs);
}

/** For the cheat-sheet overlay: the current stack, innermost (most relevant) first. */
export function useActiveShortcuts(): { label: string; shortcuts: ShortcutDef[] }[] {
  const stack = useSyncExternalStore(subscribe, snapshot, snapshot);
  return [...stack]
    .reverse()
    .map((s) => ({ label: s.label, shortcuts: s.shortcuts.current.filter((d) => !d.hidden) }))
    .filter((s) => s.shortcuts.length > 0);
}

export function formatBinding(binding: KeyBinding): string {
  const parts: string[] = [];
  if (binding.ctrl) parts.push('Ctrl');
  if (binding.alt) parts.push('Alt');
  if (binding.shift) parts.push('Shift');
  if (binding.meta) parts.push('Cmd');
  const key = binding.key;
  const label =
    key === ' '
      ? 'Space'
      : key === 'Escape'
        ? 'Esc'
        : key === 'ArrowUp'
          ? '↑'
          : key === 'ArrowDown'
            ? '↓'
            : key === 'ArrowLeft'
              ? '←'
              : key === 'ArrowRight'
                ? '→'
                : key.length === 1
                  ? key.toUpperCase()
                  : key;
  parts.push(label);
  return parts.join('+');
}

export function formatShortcut(def: ShortcutDef): string {
  return def.bindings.map(formatBinding).join(' / ');
}
